import React, { useState, useEffect, useCallback } from 'react';
import api from './api';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2d2d2d' : '#f8f9fa',
    };

    return {
        container: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginBottom: '20px',
        },
        title: {
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: colors.text,
        },
        description: {
            color: colors.subtleText,
            marginBottom: '15px',
            fontSize: '14px',
        },
        controls: {
            display: 'flex',
            gap: '10px',
            marginBottom: '15px',
            flexWrap: 'wrap',
        },
        input: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '220px',
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            backgroundColor: colors.link,
            color: 'white',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        header: {
            backgroundColor: colors.headerBg,
            color: colors.text,
            fontWeight: 'bold',
            padding: '12px 8px',
            textAlign: 'left',
            borderBottom: `2px solid ${colors.border}`,
            fontSize: '14px',
            cursor: 'pointer',
        },
        cell: {
            padding: '10px 8px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
            fontSize: '13px',
        },
        numCell: {
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
        },
        stats: {
            display: 'flex',
            gap: '20px',
            marginBottom: '15px',
            flexWrap: 'wrap',
        },
        stat: {
            backgroundColor: colors.headerBg,
            padding: '10px 15px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
        },
        statLabel: {
            fontSize: '12px',
            color: colors.subtleText,
            marginBottom: '4px',
        },
        statValue: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: colors.text,
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        loading: {
            textAlign: 'center',
            padding: '20px',
            color: colors.subtleText,
        },
        error: {
            textAlign: 'center',
            padding: '20px',
            color: '#dc3545',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '15px',
        },
    };
};

function AK0PartInstallManHoursTable({ theme, onUpload }) {
    const styles = getStyles(theme);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('part_no');
    const [sortDirection, setSortDirection] = useState('asc');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
            const response = await api.get(`/ak0-part-man-hours/?ordering=${ordering}`);
            setData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching AK0 man-hours data:', err);
            setError('Failed to fetch AK0 man-hours data');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [sortField, sortDirection]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(field === 'part_no' ? 'asc' : 'desc');
        }
    };

    const filteredData = data.filter(item => {
        if (!searchTerm) return true;
        return (item.part_no || '').toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getSortIcon = (field) => {
        if (sortField !== field) return '↕';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const formatHours = (value) => {
        if (value == null || value === '') return '—';
        const hours = Number(value);
        return Number.isFinite(hours) ? hours.toFixed(2) : '—';
    };

    const formatCount = (value) => {
        if (value == null || value === '') return '—';
        const count = Number(value);
        return Number.isFinite(count) ? String(count) : '—';
    };

    const formatDateTime = (value) => {
        if (!value) return 'N/A';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return 'Invalid Date';
        }
    };

    const latestUpload = data.reduce((latest, row) => {
        if (!row.uploaded_at) return latest;
        const ts = new Date(row.uploaded_at).getTime();
        return ts > latest ? ts : latest;
    }, 0);

    if (loading) {
        return (
            <div style={styles.container}>
                <h3 style={styles.title}>AK0 Man Hour Install Data</h3>
                <div style={styles.loading}>Loading man-hours data...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <h3 style={styles.title}>AK0 Man Hour Install Data</h3>
            <p style={styles.description}>
                Historical average removal and install man-hours by part number.
                The LTD Aircraft report adds removal hours when CRD shows the part is still installed, and always plans the install hours.
                Upload the historical workbook (Removed Part Numbers and Installed Part Numbers sheets) or a file with <strong>Part Number</strong> and <strong>Avg Man Hours</strong>.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.stats}>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Total Parts</div>
                    <div style={styles.statValue}>{filteredData.length}</div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Last Updated</div>
                    <div style={styles.statValue}>
                        {latestUpload ? formatDateTime(latestUpload) : 'N/A'}
                    </div>
                </div>
            </div>

            <div style={styles.controls}>
                <input
                    style={styles.input}
                    type="text"
                    placeholder="Search by part number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button style={styles.button} onClick={fetchData}>Refresh</button>
                {onUpload && (
                    <button
                        style={{ ...styles.button, backgroundColor: '#fd7e14' }}
                        onClick={() => onUpload(
                            'AK0 Man Hour Install',
                            'Historical workbook with Removed Part Numbers and Installed Part Numbers sheets (Average Mean MH and Work Order Count). That upload replaces the reference table. A Part Number and Avg Man Hours file still updates install hours in place.'
                        )}
                    >
                        Upload Report
                    </button>
                )}
            </div>

            {filteredData.length === 0 ? (
                <div style={styles.noData}>
                    No AK0 man-hours data found. Upload a report to get started.
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.header} onClick={() => handleSort('part_no')}>
                                    Part Number {getSortIcon('part_no')}
                                </th>
                                <th style={{ ...styles.header, textAlign: 'right' }} onClick={() => handleSort('avg_remove_hours')}>
                                    Avg Remove MH {getSortIcon('avg_remove_hours')}
                                </th>
                                <th style={{ ...styles.header, textAlign: 'right' }} onClick={() => handleSort('remove_wo_count')}>
                                    Remove WOs {getSortIcon('remove_wo_count')}
                                </th>
                                <th style={{ ...styles.header, textAlign: 'right' }} onClick={() => handleSort('avg_install_hours')}>
                                    Avg Install MH {getSortIcon('avg_install_hours')}
                                </th>
                                <th style={{ ...styles.header, textAlign: 'right' }} onClick={() => handleSort('install_wo_count')}>
                                    Install WOs {getSortIcon('install_wo_count')}
                                </th>
                                <th style={{ ...styles.header, textAlign: 'right' }} onClick={() => handleSort('avg_man_hours')}>
                                    Combined MH {getSortIcon('avg_man_hours')}
                                </th>
                                <th style={styles.header} onClick={() => handleSort('uploaded_at')}>
                                    Last Updated {getSortIcon('uploaded_at')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map(row => (
                                <tr key={row.id}>
                                    <td style={styles.cell}>{row.part_no}</td>
                                    <td style={{ ...styles.cell, ...styles.numCell }}>
                                        {formatHours(row.avg_remove_hours)}
                                    </td>
                                    <td style={{ ...styles.cell, ...styles.numCell }}>
                                        {formatCount(row.remove_wo_count)}
                                    </td>
                                    <td style={{ ...styles.cell, ...styles.numCell }}>
                                        {formatHours(row.avg_install_hours)}
                                    </td>
                                    <td style={{ ...styles.cell, ...styles.numCell }}>
                                        {formatCount(row.install_wo_count)}
                                    </td>
                                    <td style={{ ...styles.cell, ...styles.numCell }}>
                                        {formatHours(row.avg_man_hours)}
                                    </td>
                                    <td style={styles.cell}>{formatDateTime(row.uploaded_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AK0PartInstallManHoursTable;
